import React, { createContext, useContext, useState, useEffect } from 'react';
import apiClient from '../services/apiClient';
import { applyDevAccessTokenBootstrap } from '../config/devAccessTokenBootstrap';
import { loadLinkedAccountSession, clearLinkedAccountSessions } from '../utils/linkedAccountSessions';
import { queryClient } from '../lib/queryClient';

// Interfaces
interface Account {
    accountId: string;
    loginId?: string;
    displayName?: string;
    email?: string;
    profileImageUrl?: string | null;
    phoneNumber?: string;
    affiliation?: string;
    position?: string;
    department?: string;
    organizationId: string | null;
    organizationName: string;
    role: string;
    license: string;
    licenseType?: 'device' | 'user';
    isActive: boolean;
}

export interface User {
    userId: number;  // User 테이블 PK (API 호출용)
    name: string;
    email: string;
    profileImageUrl?: string | null;
    guestExpirationDate?: string | null;
    isAuthenticated: boolean;
    currentAccount: Omit<Account, 'isActive'>;
    accounts: Account[];
}

interface AuthContextType {
    user: User | null;
    isLoading: boolean;
    login: (userData: User) => void;
    logout: () => void;
    updateUserProfile: (profileData: {
        name?: string;
        email?: string;
        profileImageUrl?: string | null;
        phoneNumber?: string;
        affiliation?: string;
        position?: string;
        department?: string;
        role?: string;
        license?: string;
    }) => void;  // 프로필 업데이트 함수
    switchAccount: (accountId: string) => void;
    addAccount: (accountData: Partial<Account>) => void;
    isAuthenticated: boolean;
    getCurrentRole: () => string;
    getCurrentLicense: () => string | null;
    getLicenseInfo: () => { type: 'device' | 'user', count: number, usedCount: number };
    hasRole: (requiredRole: string) => boolean;
    canAccessScenario: (scenarioId: string) => boolean;
    canJoinClass: (classId: string, classType?: string) => boolean;
}

// Create AuthContext
const AuthContext = createContext<AuthContextType | null>(null);

// AuthProvider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    // Initialize auth state (check localStorage or session)
    useEffect(() => {
        const savedUser = localStorage.getItem('medicrew_user');
        let nextUser: User | null = null;

        if (savedUser) {
            try {
                const parsedUser = JSON.parse(savedUser) as User;
                // Check if user data is valid
                if (parsedUser && parsedUser.currentAccount) {
                    nextUser = parsedUser;
                } else {
                    // Invalid data structure - clear it
                    localStorage.removeItem('medicrew_user');
                    apiClient.clearTokens();
                }
            } catch {
                localStorage.removeItem('medicrew_user');
                apiClient.clearTokens();
            }
        }

        /** 로컬 전용: `.env.local`의 REACT_APP_DEV_ACCESS_TOKEN + REACT_APP_DEV_USER_ID 로 실 API 계정 부트스트랩 */
        const devBootstrap = applyDevAccessTokenBootstrap(!!nextUser);
        if (devBootstrap.applied && devBootstrap.user) {
            nextUser = devBootstrap.user as User;
        }

        /** 저장 사용자 JSON에 `isAuthenticated` 플래그가 없던 레거시 데이터 — 토큰이 있으면 로그인 유지 */
        const storedRefresh = localStorage.getItem('medicrew_refresh_token')?.trim();
        const storedAccess = localStorage.getItem('medicrew_access_token')?.trim();
        const hasStoredTokens = Boolean(storedAccess) || Boolean(storedRefresh);
        if (nextUser && hasStoredTokens && nextUser.isAuthenticated !== true) {
            nextUser = { ...nextUser, isAuthenticated: true };
        }

        if (nextUser) {
            setUser(nextUser);
        }
        setIsLoading(false);
    }, []);

    // Login function
    const login = (userData: User) => {
        setUser(userData);
        localStorage.setItem('medicrew_user', JSON.stringify(userData));
        // 토큰은 authService의 login 함수에서 이미 저장됨
    };

    /**
     * 로그아웃 함수
     * @description 사용자 상태 초기화 및 localStorage 정리 (모든 계정의 토큰 삭제)
     */
    const logout = () => {
        setUser(null);
        localStorage.removeItem('medicrew_user');
        localStorage.removeItem('medicrew_access_token');
        localStorage.removeItem('medicrew_refresh_token');
        localStorage.removeItem('medicrew_linked_accounts');
        // 모든 연결된 계정의 토큰 삭제
        clearLinkedAccountSessions();
    };

    /**
     * 프로필 정보 업데이트 함수
     * @description 사용자 프로필 (이름, 이메일, 프로필 이미지, role, license)을 업데이트
     * @param profileData - 업데이트할 프로필 정보
     */
    const updateUserProfile = (profileData: {
        name?: string;
        email?: string;
        profileImageUrl?: string | null;
        phoneNumber?: string;
        affiliation?: string;
        position?: string;
        department?: string;
        role?: string;
        license?: string;
    }) => {
        if (!user) return;

        const updatedUser: User = {
            ...user,
            ...(profileData.name !== undefined && { name: profileData.name }),
            ...(profileData.email !== undefined && { email: profileData.email }),
            ...(profileData.profileImageUrl !== undefined && { profileImageUrl: profileData.profileImageUrl }),
        };

        // role과 license 업데이트 (currentAccount와 accounts 모두)
        if (
            profileData.name !== undefined ||
            profileData.email !== undefined ||
            profileData.profileImageUrl !== undefined ||
            profileData.phoneNumber !== undefined ||
            profileData.affiliation !== undefined ||
            profileData.position !== undefined ||
            profileData.department !== undefined ||
            profileData.role !== undefined ||
            profileData.license !== undefined
        ) {
            updatedUser.currentAccount = {
                ...updatedUser.currentAccount,
                ...(profileData.name !== undefined && { displayName: profileData.name }),
                ...(profileData.email !== undefined && { email: profileData.email }),
                ...(profileData.profileImageUrl !== undefined && { profileImageUrl: profileData.profileImageUrl }),
                ...(profileData.phoneNumber !== undefined && { phoneNumber: profileData.phoneNumber }),
                ...(profileData.affiliation !== undefined && { affiliation: profileData.affiliation }),
                ...(profileData.position !== undefined && { position: profileData.position }),
                ...(profileData.department !== undefined && { department: profileData.department }),
                ...(profileData.role !== undefined && { role: profileData.role }),
                ...(profileData.license !== undefined && { license: profileData.license }),
            };
        }

        if (
            profileData.name !== undefined ||
            profileData.email !== undefined ||
            profileData.profileImageUrl !== undefined ||
            profileData.phoneNumber !== undefined ||
            profileData.affiliation !== undefined ||
            profileData.position !== undefined ||
            profileData.department !== undefined ||
            profileData.role !== undefined ||
            profileData.license !== undefined
        ) {
            updatedUser.accounts = updatedUser.accounts.map(acc => {
                if (acc.accountId !== updatedUser.currentAccount.accountId) return acc;

                return {
                    ...acc,
                    ...(profileData.name !== undefined && { displayName: profileData.name }),
                    ...(profileData.email !== undefined && { email: profileData.email }),
                    ...(profileData.profileImageUrl !== undefined && { profileImageUrl: profileData.profileImageUrl }),
                    ...(profileData.phoneNumber !== undefined && { phoneNumber: profileData.phoneNumber }),
                    ...(profileData.affiliation !== undefined && { affiliation: profileData.affiliation }),
                    ...(profileData.position !== undefined && { position: profileData.position }),
                    ...(profileData.department !== undefined && { department: profileData.department }),
                    ...(profileData.role !== undefined && { role: profileData.role }),
                    ...(profileData.license !== undefined && { license: profileData.license }),
                };
            });
        }

        setUser(updatedUser);
        localStorage.setItem('medicrew_user', JSON.stringify(updatedUser));
    };

    // Switch account function
    const switchAccount = (accountId: string) => {
        if (!user || !user.accounts) return;

        const targetAccount = user.accounts.find(acc => acc.accountId === accountId);
        if (!targetAccount) {
            return;
        }

        const linkedSession = loadLinkedAccountSession(accountId);
        if (linkedSession) {
            localStorage.setItem('medicrew_access_token', linkedSession.accessToken);
            if (linkedSession.refreshToken?.trim()) {
                localStorage.setItem('medicrew_refresh_token', linkedSession.refreshToken.trim());
            } else {
                localStorage.removeItem('medicrew_refresh_token');
            }
        }

        // Update accounts active status
        const updatedAccounts = user.accounts.map(acc => ({
            ...acc,
            isActive: acc.accountId === accountId
        }));

        const updatedUser: User = {
            ...user,
            name: targetAccount.displayName || targetAccount.loginId || targetAccount.email || user.name,
            email: targetAccount.email ?? user.email,
            profileImageUrl: targetAccount.profileImageUrl !== undefined
                ? targetAccount.profileImageUrl
                : user.profileImageUrl,
            accounts: updatedAccounts,
            currentAccount: {
                accountId: targetAccount.accountId,
                loginId: targetAccount.loginId,
                displayName: targetAccount.displayName,
                email: targetAccount.email,
                profileImageUrl: targetAccount.profileImageUrl,
                phoneNumber: targetAccount.phoneNumber,
                affiliation: targetAccount.affiliation,
                position: targetAccount.position,
                department: targetAccount.department,
                organizationId: targetAccount.organizationId,
                organizationName: targetAccount.organizationName,
                role: targetAccount.role,
                license: targetAccount.license
            }
        };

        setUser(updatedUser);
        localStorage.setItem('medicrew_user', JSON.stringify(updatedUser));

        // 계정 전환 시 React Query 캐시 클리어 (이전 계정의 데이터 제거)
        queryClient.clear();
    };

    // Add new account function
    const addAccount = (accountData: Partial<Account>) => {
        if (!user) return;

        const newAccount: Account = {
            accountId: `acc_${Date.now()}`,
            organizationId: accountData.organizationId || null,
            organizationName: accountData.organizationName || 'Unknown',
            role: accountData.role || 'guest',
            license: accountData.license || 'free',
            isActive: false,
            ...accountData
        };

        const updatedUser: User = {
            ...user,
            accounts: [...user.accounts, newAccount]
        };

        setUser(updatedUser);
        localStorage.setItem('medicrew_user', JSON.stringify(updatedUser));
    };

    // Helper functions
    const checkIsAuthenticated = () => {
        if (user === null) return false;
        if (user.isAuthenticated === true) return true;
        /** 레거시·파셜 상태: 컨텍스트만 보더니 토큰은 있는 경우 */
        if (typeof user.isAuthenticated !== 'boolean' && apiClient.hasTokens()) return true;
        return false;
    };

    const getCurrentRole = () => {
        const r = user?.currentAccount?.role;
        if (!r) return 'visitor';
        return String(r).trim().toLowerCase();
    };

    const getCurrentLicense = () => {
        return user?.currentAccount?.license || null;
    };

    const hasRole = (requiredRole: string) => {
        const roleHierarchy = ['visitor', 'guest', 'student', 'master', 'admin'];
        const currentRole = getCurrentRole();
        const currentIndex = roleHierarchy.indexOf(currentRole);
        const requiredIndex = roleHierarchy.indexOf(requiredRole);
        return currentIndex >= requiredIndex;
    };

    const canAccessScenario = (_scenarioId: string) => {
        return true;
    };

    const canJoinClass = (classId: string, classType = 'regular') => {
        const role = getCurrentRole();
        const license = getCurrentLicense();

        // Visitor and Guest cannot join classes (unless invited - to be implemented)
        if (role === 'visitor') return false;

        // Class license users can join organization classes for free
        if (license === 'basic_class' || license === 'pro_class') {
            // TODO: Check if class belongs to user's organization
            return true;
        }

        // Personal license users need to purchase class access
        // TODO: Check if user has purchased this specific class
        return false;
    };

    const getLicenseInfo = () => {
        // Mock data
        // For testing 'device' licensing: return { type: 'device' as const, count: 0, usedCount: 0 };
        // For testing 'user' licensing: return { type: 'user' as const, count: 50, usedCount: 10 };

        // Default to user license for now based on previous requirements
        return { type: 'user' as const, count: 50, usedCount: 10 };
    };

    const value: AuthContextType = {
        user,
        isLoading,
        login,
        logout,
        updateUserProfile,  // 🆕 프로필 업데이트 함수 추가
        switchAccount,
        addAccount,
        isAuthenticated: checkIsAuthenticated(),
        getCurrentRole,
        getCurrentLicense,
        getLicenseInfo,
        hasRole,
        canAccessScenario,
        canJoinClass
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use AuthContext
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
